import React from "react";
import { formatGtagLabel } from "../../../utils/formatGtagLabel";
import { STRINGS } from "../strings";
import { sanitizeSvg } from "../../../utils/sanitize";
import { Arrow } from "./Icons";
import {
  MegaMenuCard,
  MegaMenuCardBody,
  MegaMenuCardCta,
  MegaMenuDescription,
  MegaMenuGrid as Grid,
  MegaMenuSkeleton,
  MegaMenuTag,
  MegaMenuThumb,
  MegaMenuTitle,
  SkeletonLine,
  SkeletonLines,
  SkeletonThumb,
} from "../HeaderV2.styles";
import {
  ButtonVariant,
  IconAlignment,
  MegaMenuCardAlign,
  MegaMenuCardLayout,
  MegaMenuDensity,
  type MegaMenuAspect,
  type MegaMenuCard as MegaMenuCardData,
} from "../types";
import { MegaMenuStatus } from "../../../utils/megaMenuSource";

type MegaMenuGridProps = {
  status?: MegaMenuStatus;
  cards: MegaMenuCardData[];
  columns: number;
  aspect: MegaMenuAspect;
  layout?: MegaMenuCardLayout;
  density?: MegaMenuDensity;
  ctaLabel?: string;
  ctaVariant?: ButtonVariant;
  ctaShowIcon?: boolean;
  ctaIcon?: string;
  ctaIconAlignment?: IconAlignment;
  cardAlign?: MegaMenuCardAlign;
  htmlId?: string;
  skeletonCount?: number;
  onNavigate?: () => void;
};

export const MegaMenuGrid = ({
  status,
  cards,
  columns,
  aspect,
  layout = MegaMenuCardLayout.ABOVE,
  density = MegaMenuDensity.COMFORTABLE,
  ctaLabel,
  ctaVariant = ButtonVariant.LINK,
  ctaShowIcon = true,
  ctaIcon,
  ctaIconAlignment = IconAlignment.RIGHT,
  cardAlign = MegaMenuCardAlign.START,
  htmlId,
  skeletonCount,
  onNavigate,
}: MegaMenuGridProps) => {
  if (status === MegaMenuStatus.LOADING) {
    const count = skeletonCount ?? columns;
    return (
      <Grid $columns={columns} $density={density} $layout={layout} role="status" aria-label={STRINGS.megaMenuLoading} aria-busy="true">
        {Array.from({ length: count }, (_, i) => (
          <MegaMenuSkeleton key={i} $aspect={aspect} $layout={layout}>
            <SkeletonThumb $aspect={aspect} $layout={layout} />
            <SkeletonLines>
              <SkeletonLine $width="55%" />
              <SkeletonLine $width="80%" />
            </SkeletonLines>
          </MegaMenuSkeleton>
        ))}
      </Grid>
    );
  }

  if (cards.length === 0) return null;

  const ctaIconNode = ctaIcon ? (
    <span className="hv2-arrow" aria-hidden="true" dangerouslySetInnerHTML={{ __html: sanitizeSvg(ctaIcon) }} />
  ) : (
    <Arrow className="hv2-arrow" />
  );

  return (
    <Grid $columns={columns} $density={density} $layout={layout}>
      {cards.map((card) => (
        <li key={card.id}>
          <MegaMenuCard
            href={card.url}
            $layout={layout}
            aria-label={card.title ? undefined : card.label}
            onClick={onNavigate}
            data-testid="header-v2-megamenu-card"
            data-gtag-action="Clique Menu"
            data-gtag-label={formatGtagLabel(htmlId, card.label)}
            data-gtag-category="Cabeçalho"
          >
            <MegaMenuThumb $aspect={aspect} $layout={layout} className="hv2-thumb">
              {card.image ? <img src={card.image.src} alt="" loading="lazy" data-testid="header-v2-megamenu-card-image" /> : null}
            </MegaMenuThumb>

            <MegaMenuCardBody $align={layout === MegaMenuCardLayout.ABOVE ? cardAlign : MegaMenuCardAlign.START}>
              {card.tag && <MegaMenuTag>{card.tag}</MegaMenuTag>}
              {card.title && (
                <MegaMenuTitle as="span" className="hv2-title">
                  {card.title}
                </MegaMenuTitle>
              )}
              {card.description && <MegaMenuDescription title={card.description}>{card.description}</MegaMenuDescription>}
              {ctaLabel && (
                <MegaMenuCardCta aria-hidden="true" $variant={ctaVariant}>
                  {ctaShowIcon && ctaIconAlignment === IconAlignment.LEFT && ctaIconNode}
                  {ctaLabel}
                  {ctaShowIcon && ctaIconAlignment === IconAlignment.RIGHT && ctaIconNode}
                </MegaMenuCardCta>
              )}
            </MegaMenuCardBody>
          </MegaMenuCard>
        </li>
      ))}
    </Grid>
  );
};
